import { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo, useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react'

import { TaxRate } from '@/api/admin-country-types'
import { Price, PriceInput } from '@/api/admin-products-types'
import { SalesChannelListItem } from '@/api/admin-sales-channels-types'
import { useApiContext } from '@/api/react-query'
import { toast } from '@/components/base/toast'
import { useI18n } from '@/i18n/i18n-react'
import { fromPrecision, getCurrencyDecimals, toPrecision } from '@/utils/currency'
import { sortArray } from '@/utils/utilities'

const disabledCellStyle: ColDef<RowData>['cellStyle'] = params =>
	params.data?.enabled ? null : { color: '#888', backgroundColor: '#f5f5f5' }

export type RowData = {
	enabled: boolean
	salesChannelUuid: string
	salesChannel: string
	currencyCode: string
	price: number | null
	taxRate: string
	taxInclusive: boolean
}

export interface FeePricesGridRef {
	getGridData: () => PriceInput[] | null
}

interface FeePricesGridProps {
	initialPrices?: Price[]
	salesChannelsData: SalesChannelListItem[]
	taxRatesData: TaxRate[]
}

export const FeePricesGrid = forwardRef<FeePricesGridRef, FeePricesGridProps>(
	(
		{
			initialPrices = [],
			salesChannelsData,
			taxRatesData
		}: {
			initialPrices?: Price[]
			salesChannelsData: SalesChannelListItem[]
			taxRatesData: TaxRate[]
		},
		ref
	) => {
		const { L } = useI18n()
		const gridRef = useRef<AgGridReact<any>>(null)
		const [rowData, setRowData] = useState<RowData[]>([])

		const [taxRates, setTaxRates] = useState<TaxRate[]>([])
		const [salesChannels, setSalesChannels] = useState<SalesChannelListItem[]>([])
		const [loading, setLoading] = useState(false)
		const { api } = useApiContext()

		useEffect(() => {
			try {
				setLoading(true)
				setTaxRates(sortArray(taxRatesData, x => x.name))
				sortArray(salesChannelsData, x => x.name)
				setSalesChannels(salesChannelsData)
				const data: RowData[] = []
				for (const salesChannel of salesChannelsData) {
					const initialPrice = initialPrices.find(x => x.salesChannel.uuid === salesChannel.uuid)
					data.push({
						enabled: !!initialPrice?.priceInfo,
						salesChannelUuid: salesChannel.uuid,
						salesChannel: salesChannel.name,
						currencyCode: salesChannel.currencyCode,
						price:
							initialPrice?.priceInfo && !initialPrice.priceInfo.variablePrice
								? fromPrecision(initialPrice.priceInfo.amount)
								: 0,
						taxRate: taxRatesData[0].name,
						taxInclusive: initialPrice?.priceInfo?.taxInclusive ?? true
					})
				}
				setRowData(data)
				setLoading(false)
			} catch (e) {
				console.error(e)
			}
		}, [])

		const colDefs = useMemo<ColDef<RowData>[]>(
			() =>
				[
					{
						field: 'enabled',
						headerName: L.General.Enabled(),
						editable: true,
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						width: 100,
						flex: 0
					},
					{
						field: 'salesChannel',
						headerName: L.Fields.SalesChannel(),
						editable: false
					},
					{
						field: 'price',
						headerName: L.Fields.Price(),
						editable: true,
						cellEditor: 'agNumberCellEditor',
						cellEditorParams: {
							min: 0,
							precision: 2
						},
						cellStyle: disabledCellStyle,
						valueFormatter: params =>
							L.Amount.Default({
								amount: params.value ?? 0,
								currencyCode: params.data?.currencyCode ?? ''
							})
					},
					{
						field: 'taxRate',
						headerName: L.Fields.TaxRate(),
						cellEditor: 'agSelectCellEditor',
						cellEditorParams: {
							values: taxRates.map(x => x.name)
						},
						cellStyle: disabledCellStyle
					},
					{
						field: 'taxInclusive',
						headerName: L.Fields.TaxInclusive(),
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						cellStyle: disabledCellStyle
					}
				] as ColDef<RowData>[],
			[taxRates, L]
		)

		const defaultColDef = useMemo<ColDef>(() => {
			return {
				editable: params => params.data.enabled,
				flex: 1
			}
		}, [])

		useImperativeHandle(ref, () => ({
			getGridData: () => {
				const allRowData: RowData[] = []
				gridRef.current?.api.stopEditing()
				gridRef.current?.api.forEachNode(node => {
					allRowData.push(node.data)
				})

				const newPrices: PriceInput[] = []
				for (const row of allRowData) {
					const taxRate = taxRates.find(x => x.name === row.taxRate)
					if (!taxRate) {
						toast.error(L.Product.Pricing.InvalidTaxRate({ taxRate: row.taxRate }))
						return null
					}
					if (!row.enabled) {
						continue
					}
					newPrices.push({
						salesChannelUuid: row.salesChannelUuid,
						currencyCode: row.currencyCode,
						priceInfo: {
							variablePrice: false,
							amount: row.price
								? toPrecision(row.price, getCurrencyDecimals(row.currencyCode))
								: toPrecision(0),
							taxRateUuid: taxRate.uuid,
							taxInclusive: row.taxInclusive
						}
					})
				}
				return newPrices
			}
		}))

		return (
			<div style={{ height: 500 }} className='ag-theme-quartz'>
				<AgGridReact
					ref={gridRef}
					loading={loading}
					rowData={rowData}
					columnDefs={colDefs}
					defaultColDef={defaultColDef}
					singleClickEdit
					onCellValueChanged={e => {
						if (e.colDef.field === 'price') {
							const enabled = typeof e.newValue === 'number' && e.newValue > 0
							if (e.data.enabled !== enabled) {
								e.node.setDataValue('enabled', enabled)
								return
							}
						}
						if (e.colDef.field === 'enabled') {
							// Create a new array reference to trigger re-render for cellStyle
							setRowData(prevRowData => [...prevRowData])
						}
					}}
				/>
			</div>
		)
	}
)
